// Change a link after it is made (2026-09-24): its name, who it is from, and
// for a capture link the page itself (heading, a line under it, one-tap
// starters, extra fields), the private note for Prime, a daily cap, a pause,
// and how long it lasts. The secret never changes, so nobody needs a new link.
import { useEffect, useState } from 'react'
import type { GrantResource } from '@/kinds/grant'
import { memberScope } from '@/kinds/grant/schema'
import { usePeople } from '@/db/hooks'
import { api, mutate } from '@/runtime'
import { Button, Field, Segmented, Sheet, cx } from './ui'
import { FromField } from './FromField'

const FIELDS: { key: 'details' | 'when' | 'link'; label: string; hint: string }[] = [
  { key: 'details', label: 'Details', hint: 'A bigger box under the main line' },
  { key: 'when', label: 'When', hint: 'A day, and a time if they want' },
  { key: 'link', label: 'Link', hint: 'A web address' },
]

/** A few emoji to start from; any one typed in works too. */
const EMOJI = ['🍳', '🛒', '🎁', '📝', '🏡', '❤️', '📦', '🎉']

/**
 * The real preview card, drawn by the server for the words as they are now
 * (a moment after the last keystroke), so what is set here is what WhatsApp shows.
 */
const CardPreview = ({ title, sub, icon }: { title: string; sub: string; icon: string }) => {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let made: string | null = null
    let stale = false
    const t = window.setTimeout(() => {
      api
        .previewCard({ title, sub, icon })
        .then((b) => {
          if (stale) return
          made = URL.createObjectURL(b)
          setUrl(made)
        })
        .catch(() => {})
    }, 350)
    return () => {
      stale = true
      window.clearTimeout(t)
      if (made) URL.revokeObjectURL(made)
    }
  }, [title, sub, icon])
  return (
    <div className="mb-3 overflow-hidden rounded-[var(--radius-card-inner)] border border-line bg-raised aspect-[1200/630]">
      {url && (
        <img
          src={url}
          alt="How the link looks when it is shared"
          className="w-full h-full object-cover"
          data-testid="grant-card-preview"
        />
      )}
    </div>
  )
}

const addDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString()

export const GrantSheet = ({ grant, onClose }: { grant: GrantResource; onClose: () => void }) => {
  const capture = grant.spec.scopes.includes('capture')
  const member = grant.spec.scopes.length > 0 && grant.spec.scopes.every((s) => memberScope(s) !== null)
  const [name, setName] = useState(grant.spec.name)
  const [from, setFrom] = useState(grant.spec.from ?? '')
  const [purpose, setPurpose] = useState(grant.spec.purpose ?? '')
  const [intro, setIntro] = useState(grant.spec.intro ?? '')
  const [reply, setReply] = useState(grant.spec.reply ?? '')
  const [buttons, setButtons] = useState((grant.spec.buttons ?? []).join('\n'))
  const [fields, setFields] = useState<string[]>(grant.spec.fields ?? [])
  const [context, setContext] = useState(grant.spec.context ?? '')
  const [limit, setLimit] = useState(grant.spec.daily_limit ?? 30)
  const [paused, setPaused] = useState(!!grant.spec.paused)
  const [life, setLife] = useState<'keep' | 'week' | 'month' | 'forever'>('keep')
  const [cardTitle, setCardTitle] = useState(grant.spec.card_title ?? '')
  const [cardText, setCardText] = useState(grant.spec.card_text ?? '')
  const [cardIcon, setCardIcon] = useState(grant.spec.card_icon ?? '')
  const owner =
    usePeople()
      .find((p) => p.spec.me)
      ?.spec.name.split(/\s+/)[0] ?? 'Alfie'
  // what the card says when its own words are left empty: the page's
  const shownTitle = cardTitle.trim() || purpose.trim() || `Send ${owner} a note`
  const shownText = cardText.trim() || intro.trim() || `Whatever you send lands with ${owner}.`
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    setError(null)
    if (!name.trim()) return setError('Give it a name')
    const next: Record<string, unknown> = { name: name.trim() }
    if (member) next.from = from.trim().toLowerCase() || undefined
    if (capture) {
      const starters = buttons
        .split('\n')
        .map((b) => b.trim().slice(0, 40))
        .filter(Boolean)
        .slice(0, 6)
      Object.assign(next, {
        from: from.trim().toLowerCase() || undefined,
        purpose: purpose.trim() || undefined,
        intro: intro.trim() || undefined,
        reply: reply.trim() || undefined,
        reply_at: reply.trim()
          ? reply.trim() === (grant.spec.reply ?? '')
            ? grant.spec.reply_at
            : new Date().toISOString()
          : undefined,
        buttons: starters.length ? starters : undefined,
        fields: fields.length ? fields : undefined,
        context: context.trim() || undefined,
        daily_limit: limit === 30 ? undefined : limit,
        paused: paused || undefined,
        card_title: cardTitle.trim() || undefined,
        card_text: cardText.trim() || undefined,
        card_icon: cardIcon.trim() || undefined,
      })
    }
    if (life !== 'keep')
      next.expires_at = life === 'week' ? addDays(7) : life === 'month' ? addDays(30) : undefined
    const patch: Record<string, unknown> = {}
    const was = grant.spec as Record<string, unknown>
    for (const [k, v] of Object.entries(next))
      if (JSON.stringify(was[k]) !== JSON.stringify(v)) patch[k] = v === undefined ? null : v
    try {
      if (Object.keys(patch).length) await mutate.patch(grant.id, { spec: patch })
      onClose()
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Edit link"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} data-testid="grant-save">
            Save
          </Button>
        </>
      }
    >
      <Field label="Name" hint="Only you see this.">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full"
          data-testid="grant-name"
        />
      </Field>
      {member && (
        <FromField
          value={from}
          onChange={setFrom}
          label="Who"
          hint="Links this login to their record, so “Share with…” can notify them and put things in their app."
          testId="grant-from"
        />
      )}
      {capture && (
        <>
          <FromField value={from} onChange={setFrom} testId="grant-from" />
          <h3 className="text-[13px] font-semibold text-muted mt-5 mb-2">Their page</h3>
          <Field label="Heading" hint="Leave empty for “Send Alfie a note”.">
            <input
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              maxLength={300}
              placeholder="What should we have for dinner this week?"
              className="w-full"
              data-testid="grant-purpose"
            />
          </Field>
          <Field label="A line under it">
            <input
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              maxLength={300}
              placeholder="One idea at a time is perfect"
              className="w-full"
              data-testid="grant-intro"
            />
          </Field>
          <Field
            label="A note for them"
            hint="Your latest word to them, shown on their page. Only this; nothing else of yours."
          >
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              maxLength={280}
              rows={2}
              placeholder="Got it, thanks. Dinner is at 7."
              className="w-full"
              data-testid="grant-reply"
            />
          </Field>
          <Field label="Quick buttons" hint="One per line, up to six. A tap starts their message with it.">
            <textarea
              value={buttons}
              onChange={(e) => setButtons(e.target.value)}
              rows={3}
              placeholder={'Shopping\nCall me\nPick up…'}
              className="w-full"
              data-testid="grant-buttons"
            />
          </Field>
          <div className="mb-3">
            <span className="block text-[13px] font-semibold text-muted mb-1.5">Extra fields</span>
            <ul className="group-list">
              {FIELDS.map((f) => (
                <li key={f.key}>
                  <label className="flex items-center gap-3 px-3.5 min-h-12 py-1.5">
                    <input
                      type="checkbox"
                      checked={fields.includes(f.key)}
                      onChange={(e) =>
                        setFields((cur) =>
                          e.target.checked ? [...cur, f.key] : cur.filter((x) => x !== f.key),
                        )
                      }
                      data-testid={`grant-field-${f.key}`}
                    />
                    <span className="flex-1">
                      <span className="block text-[15px]">{f.label}</span>
                      <span className="block text-xs text-faint">{f.hint}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
          <h3 className="text-[13px] font-semibold text-muted mt-5 mb-2">When it’s shared</h3>
          <p className="text-xs text-faint -mt-1 mb-2">
            The card WhatsApp, iMessage and the like show for the link.
          </p>
          <CardPreview title={shownTitle} sub={shownText} icon={cardIcon} />
          <Field label="Card title" hint="Leave empty to use the page’s heading.">
            <input
              value={cardTitle}
              onChange={(e) => setCardTitle(e.target.value)}
              maxLength={120}
              placeholder={purpose.trim() || `Send ${owner} a note`}
              className="w-full"
              data-testid="grant-card-title"
            />
          </Field>
          <Field label="Card text" hint="Leave empty to use the line under the heading.">
            <input
              value={cardText}
              onChange={(e) => setCardText(e.target.value)}
              maxLength={200}
              placeholder={intro.trim() || `Whatever you send lands with ${owner}.`}
              className="w-full"
              data-testid="grant-card-text"
            />
          </Field>
          <div className="mb-3">
            <span className="block text-[13px] font-semibold text-muted mb-1.5">Emoji</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {EMOJI.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setCardIcon(cardIcon === e ? '' : e)}
                  aria-pressed={cardIcon === e}
                  className={cx(
                    'h-10 w-10 rounded-full border text-xl leading-none',
                    cardIcon === e ? 'border-accent bg-accent-soft' : 'border-line bg-raised',
                  )}
                >
                  {e}
                </button>
              ))}
              <input
                value={EMOJI.includes(cardIcon) ? '' : cardIcon}
                onChange={(e) => setCardIcon(e.target.value.trim().slice(0, 16))}
                placeholder="Other"
                className="w-20 text-center"
                aria-label="Another emoji"
                data-testid="grant-card-icon"
              />
            </div>
          </div>
          <h3 className="text-[13px] font-semibold text-muted mt-5 mb-2">Behind the scenes</h3>
          <Field
            label="Note for Prime"
            hint="Rides along with everything sent through this link. They never see it."
          >
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full"
              data-testid="grant-context"
            />
          </Field>
          <Field label="Most a day">
            <Segmented
              label="Most a day"
              value={limit}
              options={[
                { value: 10, label: '10' },
                { value: 30, label: '30' },
                { value: 100, label: '100' },
              ]}
              onChange={setLimit}
            />
          </Field>
          <label className="flex items-center gap-3 min-h-11 mb-3 text-[15px]">
            <input
              type="checkbox"
              checked={paused}
              onChange={(e) => setPaused(e.target.checked)}
              data-testid="grant-paused"
            />
            <span>
              Pause this link
              <span className="block text-xs text-faint">
                The page stays, but says it’s paused and takes nothing.
              </span>
            </span>
          </label>
        </>
      )}
      <Field
        label="Lasts"
        hint={
          grant.spec.expires_at
            ? `Now until ${new Date(grant.spec.expires_at).toLocaleDateString()}`
            : 'Now it never ends.'
        }
      >
        <Segmented
          label="Lasts"
          value={life}
          options={[
            { value: 'keep', label: 'As it is' },
            { value: 'week', label: 'A week' },
            { value: 'month', label: 'A month' },
            { value: 'forever', label: 'No end' },
          ]}
          onChange={setLife}
        />
      </Field>
      {error && <p className={cx('text-sm text-rose-300 mb-3')}>{error}</p>}
    </Sheet>
  )
}
